import { makeAutoObservable, runInAction } from 'mobx';
import type RootStore from '../../stores/RootStore';
import type { Product, ProductCategory, ProductVariant } from '../../types/Product';
import type ServiceStore from '../../stores/ServiceStore';

// For now, let's just reference the store through the RootStore interface instead of importing the class
// We'll fix this circular dependency later
// import type ProductStore from './ProductStore';

class ProductViewStore {
  rootStore: RootStore;

  // UI State
  selectedTab = 'overview';
  selectedCategoryId: number | null = null;
  selectedProductType: string | null = null;
  searchQuery = '';

  // Filters
  filters = {
    productType: '',
    categoryId: null as number | null,
    active: true,
    inStock: false
  };

  // Pagination
  currentPage = 0;
  pageSize = 25;

  // Product Detail State
  selectedProductId: number | null = null;
  selectedVariantId: number | null = null;
  selectedBOMId: number | null = null;

  // Dialog States
  showProductForm = false;
  showVariantForm = false;
  showBOMForm = false;
  showPricingForm = false;
  showInventoryForm = false;
  showMovementDialog = false;

  // Form Data
  editingProduct: Product | null = null;
  editingVariant: ProductVariant | null = null;

  constructor(rootStore: RootStore) {
    makeAutoObservable(this);
    this.rootStore = rootStore;
  }

  // Navigation Methods
  setSelectedTab(tab: string) {
    this.selectedTab = tab;
  }

  setSelectedCategory(categoryId: number | null) {
    runInAction(() => {
      this.selectedCategoryId = categoryId;
      this.filters.categoryId = categoryId;
      this.currentPage = 0;
    });

    // Reload products with new filter
    (this.rootStore.productStore as any)?.loadProducts(this.buildQuery());
  }

  setSelectedProductType(productType: string | null) {
    runInAction(() => {
      this.selectedProductType = productType;
      this.filters.productType = productType || '';
      this.currentPage = 0;
    });

    // Reload products with new filter
    (this.rootStore.productStore as any)?.loadProducts(this.buildQuery());
  }

  setSearchQuery(query: string) {
    runInAction(() => {
      this.searchQuery = query;
      this.currentPage = 0;
    });

    // Debounced search could be implemented here
    (this.rootStore.productStore as any)?.loadProducts(this.buildQuery());
  }

  updateFilter(key: string, value: any) {
    runInAction(() => {
      (this.filters as any)[key] = value;
      this.currentPage = 0;
    });

    (this.rootStore.productStore as any)?.loadProducts(this.buildQuery());
  }

  // Product Selection
  async selectProduct(productId: number) {
    runInAction(() => {
      this.selectedProductId = productId;
      this.selectedVariantId = null;
      this.selectedBOMId = null;
    });

    // Load product details
    await (this.rootStore.productStore as any)?.getProduct(productId);

    // Load related data
    await Promise.all([
      (this.rootStore.productStore as any)?.loadVariants(productId),
      (this.rootStore.productStore as any)?.loadBOMs(productId),
      (this.rootStore.productStore as any)?.loadPricing(productId),
      (this.rootStore.productStore as any)?.loadInventory(productId)
    ]);
  }

  selectVariant(variantId: number | null) {
    runInAction(() => {
      this.selectedVariantId = variantId;
    });

    // Reload pricing and inventory for specific variant
    if (this.selectedProductId) {
      (this.rootStore.productStore as any)?.loadPricing(this.selectedProductId, variantId || undefined);
      (this.rootStore.productStore as any)?.loadInventory(this.selectedProductId, variantId || undefined);
    }
  }

  selectBOM(bomId: number | null) {
    runInAction(() => {
      this.selectedBOMId = bomId;
    });

    // Load BOM items
    if (bomId) {
      (this.rootStore.productStore as any)?.loadBOMItems(bomId);
    }
  }

  // Pagination
  setPage(page: number) {
    runInAction(() => {
      this.currentPage = page;
    });

    (this.rootStore.productStore as any)?.loadProducts(this.buildQuery());
  }

  setPageSize(size: number) {
    runInAction(() => {
      this.pageSize = size;
      this.currentPage = 0;
    });

    (this.rootStore.productStore as any)?.loadProducts(this.buildQuery());
  }

  // Dialog Management
  openProductForm(product?: Product) {
    runInAction(() => {
      this.editingProduct = product || null;
      this.showProductForm = true;
    });
  }

  closeProductForm() {
    runInAction(() => {
      this.editingProduct = null;
      this.showProductForm = false;
    });
  }

  openVariantForm(variant?: ProductVariant) {
    runInAction(() => {
      this.editingVariant = variant || null;
      this.showVariantForm = true;
    });
  }

  closeVariantForm() {
    runInAction(() => {
      this.editingVariant = null;
      this.showVariantForm = false;
    });
  }

  openBOMForm() {
    runInAction(() => {
      this.showBOMForm = true;
    });
  }

  closeBOMForm() {
    runInAction(() => {
      this.showBOMForm = false;
    });
  }

  openPricingForm() {
    runInAction(() => {
      this.showPricingForm = true;
    });
  }

  closePricingForm() {
    runInAction(() => {
      this.showPricingForm = false;
    });
  }

  openInventoryForm() {
    runInAction(() => {
      this.showInventoryForm = true;
    });
  }

  closeInventoryForm() {
    runInAction(() => {
      this.showInventoryForm = false;
    });
  }

  openMovementDialog() {
    runInAction(() => {
      this.showMovementDialog = true;
    });
  }

  closeMovementDialog() {
    runInAction(() => {
      this.showMovementDialog = false;
    });
  }

  // Query Building
  private buildQuery() {
    const query: any = {
      active: this.filters.active,
      $skip: this.currentPage * this.pageSize,
      $limit: this.pageSize,
      $sort: { name: 1 }
    };

    if (this.filters.productType) {
      query.productType = this.filters.productType;
    }

    if (this.filters.categoryId) {
      query.categoryId = this.filters.categoryId;
    }

    if (this.searchQuery) {
      query.$or = [
        { name: { $ilike: `%${this.searchQuery}%` } },
        { productCode: { $ilike: `%${this.searchQuery}%` } },
        { description: { $ilike: `%${this.searchQuery}%` } }
      ];
    }

    return query;
  }

  // Reset Methods
  resetFilters() {
    runInAction(() => {
      this.filters = {
        productType: '',
        categoryId: null,
        active: true,
        inStock: false
      };
      this.selectedCategoryId = null;
      this.selectedProductType = null;
      this.searchQuery = '';
      this.currentPage = 0;
    });

    (this.rootStore.productStore as any)?.loadProducts(this.buildQuery());
  }

  resetSelection() {
    runInAction(() => {
      this.selectedProductId = null;
      this.selectedVariantId = null;
      this.selectedBOMId = null;
      this.selectedTab = 'overview';
    });
  }

  // Computed Getters
  get selectedProduct() {
    return (this.rootStore.productStore as any)?.selectedProduct;
  }

  get selectedVariant() {
    return (this.rootStore.productStore as any)?.variants.find(v => v.id === this.selectedVariantId);
  }

  get selectedBOM() {
    return (this.rootStore.productStore as any)?.billOfMaterials.find(bom => bom.id === this.selectedBOMId);
  }

  get filteredCategories() {
    return (this.rootStore.productStore as any)?.categoryTree || [];
  }

  get hasFilters() {
    return !!(
      this.filters.productType ||
      this.filters.categoryId ||
      this.searchQuery ||
      this.filters.inStock
    );
  }

  get totalPages() {
    // This would need to be calculated based on total count from API
    return Math.ceil(((this.rootStore.productStore as any)?.products.length || 0) / this.pageSize);
  }

  get canEditProduct() {
    // Check permissions
    return true; // Implement permission checking
  }

  get canManageInventory() {
    // Check permissions
    return true; // Implement permission checking
  }

  get canManagePricing() {
    // Check permissions
    return true; // Implement permission checking
  }
}

export default ProductViewStore;